// WP-P2 product-surface helpers. Kept dependency-free so upload/export contracts can be
// tested without rendering the classifier.

const norm = v => String(v ?? "").trim();
const money = v => {
  if (v === "" || v == null) return null;
  const n = Number(String(v).replace(/[$,]/g, ""));
  return Number.isFinite(n) ? n : null;
};

const find = (headers, tests) => headers.findIndex(h => tests.some(t => typeof t === "string" ? h === t : t.test(h)));

export function vehicleColumns(headers) {
  return {
    make: find(headers, ["make", /^vehicle make$/]),
    model: find(headers, ["model", /^vehicle model$/]),
    year: find(headers, ["year", /^(?:model|vehicle) year$/]),
    mileage: find(headers, ["mileage", "odometer", /^vehicle miles?$/, /^miles$/]),
  };
}

export function applyRoVehicle(base, raw, columns, inherited = {}) {
  for (const key of ["make", "model", "year", "mileage"]) {
    const value = columns[key] >= 0 ? norm(raw[columns[key]]) : "";
    if (value) inherited[key] = key === "mileage" ? (money(value) ?? value) : value;
    if (inherited[key] !== undefined && inherited[key] !== "") base[key] = inherited[key];
  }
  base.vehicleMake = base.make || "";
  return base;
}

export function partsColumns(headers) {
  return {
    ro: find(headers, [/repair order/, /^ro(?:\s*(?:number|no|#))?$/]),
    line: find(headers, [/operation line/, /^op line/, /^line(?:\s*(?:number|no|#))?$/]),
    partNumber: find(headers, [/^part\s*(?:number|no|#)$/, /^part number/]),
    description: find(headers, [/^part\s*(?:description|desc|name)/]),
    sale: find(headers, [/^part\s*(?:sale|sales|selling price|amount)$/, /^sale(?: price)?$/]),
    cost: find(headers, [/^part\s*(?:cost|unit cost)$/, /^cost$/]),
    brand: find(headers, [/^brand$/, /^manufacturer$/, /^part manufacturer$/]),
    sourceVendor: find(headers, [/^vendor$/, /^source vendor$/, /^supplier$/]),
    oemIndicator: find(headers, [/^oem(?: flag| indicator| status)?$/, /^is oem$/]),
    quantity: find(headers, [/^qty$/, /^quantity$/]),
  };
}

export function parsePartsRows(raw) {
  const headers = (raw[0] || []).map(v => norm(v).toLowerCase());
  const c = partsColumns(headers);
  if (c.ro < 0 || (c.description < 0 && c.partNumber < 0)) {
    return { error: "Parts file needs a repair-order column and a part number or description column.", map: {} };
  }
  const map = {}; let lastRO = "";
  for (const row of raw.slice(1)) {
    const explicitRo = norm(row[c.ro]); if (explicitRo) lastRO = explicitRo;
    const ro = explicitRo || lastRO; const line = c.line >= 0 ? norm(row[c.line]) : "";
    const partNumber = c.partNumber >= 0 ? norm(row[c.partNumber]) : "";
    const partDesc = c.description >= 0 ? norm(row[c.description]) : "";
    if (!ro || (!partNumber && !partDesc)) continue;
    const key = `${ro}:${line}`; if (!map[key]) map[key] = [];
    map[key].push({ ro, line, partNumber, partDesc,
      sale: c.sale >= 0 ? money(row[c.sale]) : null,
      cost: c.cost >= 0 ? money(row[c.cost]) : null,
      brand: c.brand >= 0 ? norm(row[c.brand]) : "",
      sourceVendor: c.sourceVendor >= 0 ? norm(row[c.sourceVendor]) : "",
      oemIndicator: c.oemIndicator >= 0 ? norm(row[c.oemIndicator]) : null,
      quantity: c.quantity >= 0 ? money(row[c.quantity]) : null,
    });
  }
  return { error: null, map, columns: c };
}

export const MONEY_EXPORT_COLUMNS = new Set([6, 7, 9, 10, 11, 12, 15, 16, 17]);
export function numericExportValue(value) {
  return money(value);
}
